import { CamelCaseKeys, NumericString, WithParams } from '@utils/typescript'
import { array, boolean, number, object, string } from 'yup';
import { Company } from './Company';
import { InsurerLess } from './Insurer';
import { OrderCustomTypes, OrderStatuses } from './Order';
import { Genders, User } from './User';

export type QuoteOptionParams = WithParams<{
  activation_date: string;
  me: {
    dob: string;
    gender: Genders;
  };
  spouse?: {
    dob: string;
  };
  sons?: {
    dob: string;
  }[];
  daughters?: {
    dob: string;
  }[];
}>;

export const quoteOptionParamsSchema = object({
  activationDate: string(),
  me: object({
    gender: string(),
    dob: string(),
  }),
  spouse: object({
    dob: string(),
  }).default(undefined),
  sons: array().of(
    object({
      dob: string(),
    }),
  ),
  daughters: array().of(
    object({
      dob: string(),
    }),
  ),
});

export type QuoteUserParams = WithParams<{
  name: string;
  name_km: string;
  gender: string;
  dob: string;
  personal_code: string;
  occupation: string;
  email: string;
  address: string;
  mobile_number: string;
  no_mobile_number: boolean;
}>;

export type QuoteMemberParams = WithParams<{
  name: string;
  name_km?: string;
  dob: string;
  age?: number;
  gender: Genders;
  height: number;
  weight: number;
  bmi?: number;
  no_mobile_number: boolean;
  mobile_number: string;
  email: string;
  address: string;
  occupation: string;
  personal_code?: string;
  medical_answers: {
    answer: 'yes' | 'no' | '';
    remark?: string;
  }[];
}>;

export const quoteMemberParamsSchema = object({
  name: string(),
  nameKm: string(),
  dob: string(),
  age: number(),
  gender: string(),
  height: number(),
  weight: number(),
  bmi: number(),
  noMobileNumber: boolean(),
  mobileNumber: string(),
  email: string(),
  address: string(),
  occupation: string(),
  personalCode: string(),
  medicalAnswers: array().of(
    object({
      answer: string(),
      remark: string()
    })
  )
});

export type QuoteMember = CamelCaseKeys<{
  member_type: string;
  name?: string;
  name_km?: string;
  dob: string;
  age: number;
  gender: Genders;
  height?: number;
  weight?: number;
  bmi?: number;
  no_mobile_number: boolean;
  mobile_number: string;
  email?: string;
  address?: string;
  personal_code?: string;
  occupation?: string;
  premium: NumericString;
  discount_premium: NumericString;
  final_premium: NumericString;
  medical_answers: {
    answer: 'yes' | 'no';
    remark?: string;
  }[];
}>;

export type QuotablePremium = CamelCaseKeys<{
  admin_fee_amount: NumericString;
  admin_fee_final_amount: NumericString;
  premium: NumericString;
  final_premium: NumericString;
  total_premium: NumericString;
  total_premium_khr: NumericString;
}>

export type QuotableExclusion = CamelCaseKeys<{
  exclusion_html: string;
}>;

export type QuotableDocument = CamelCaseKeys<{
  policy_wording_file?: string;
  brochure_file?: string;
  terms_and_conditions_file?: string;
}>;

export type QuotableList = QuotablePremium & CamelCaseKeys<{
  insurer: InsurerLess;
}>;

export type Quotable = QuotableList & QuotableDocument & QuotableExclusion & CamelCaseKeys<{
  company?: Company;
  user: User;
  frontline_user: User;
  status: OrderStatuses;
  order_encrypted_id: string;
  custom_type: OrderCustomTypes;
  order_number: string;
  policy_number?: string;
  started_at?: number;
  ended_at?: number;
  activation_date?: string;
  note: string;
  proof?: string;
  customer_consent?: string;
  occupation_declaration_description: string;
}>;
